import { MouseEvent, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { ChevronDown, Loader2, MoveLeft, Plus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { ScrollArea } from 'components/ui/scroll-area';
import { Textarea } from 'components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import {
  HelpDeskFormData,
  HelpDeskValidator,
} from 'lib/validators/helpdesk-ticket';
import { useAddHelpDeskTicket } from 'services/help-desk/hooks';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { PATH_ROUTES } from 'routes/path';

export default function AddHelpDeskTicketPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const fileInputRef = useRef(null);

  const { mutate, isPending: isCreating } = useAddHelpDeskTicket({
    onSuccess() {
      navigate(PATH_ROUTES.HELP_DESK);
      toast.success(t('ticket_creation_msg'));
    },
  });

  const form = useForm<HelpDeskFormData>({
    resolver: zodResolver(HelpDeskValidator),
    defaultValues: {
      subject: '',
      description: '',
      tags: [],
      priority: 'Medium',
      attachments: [],
    },
  });

  function onSubmit(values: HelpDeskFormData) {
    const formData = new FormData();
    formData.append('subject', values.subject);
    formData.append('description', values?.description || '');
    formData.append('priority', values.priority);
    formData.append('tags', JSON.stringify(values.tags));
    values.attachments.forEach((attachment: any, index: number) => {
      formData.append(`file_${index + 1}`, attachment);
    });
    mutate(formData);
  }

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (fileInputRef.current) {
      (fileInputRef.current as HTMLInputElement).click();
    }
  };

  const subjectOptions = [
    { label: t('ticket_subject_1'), tags: ['Facescan', 'MobileApp'] },
    {
      label: t('ticket_subject_2'),
      tags: ['Facescan', 'MobileApp'],
    },
    {
      label: t('ticket_subject_3'),
      tags: ['PersonalisedHealthReport', 'MobileApp'],
    },
    {
      label: t('ticket_subject_4'),
      tags: ['LabReportAnalysis', 'MobileApp'],
    },
    {
      label: t('ticket_subject_5'),
      tags: ['Sub-profile', 'MobileApp'],
    },
    {
      label: t('ticket_subject_6'),
      tags: ['LabReportAnalysis', 'MobileApp'],
    },
    {
      label: t('ticket_subject_7'),
      tags: ['AdditionalQuestionnaire', 'MobileApp'],
    },
    {
      label: t('ticket_subject_8'),
      tags: ['ProfileUpdate', 'MobileApp'],
    },
    { label: t('ticket_subject_9'), tags: ['Other', 'MobileApp'] },
  ];

  const priorities = [
    { label: t('low'), value: 'Low' },
    { label: t('medium'), value: 'Medium' },
    { label: t('high'), value: 'High' },
  ];

  return (
    <div className='h-fit py-8 lg:h-[calc(100vh-6rem)]'>
      <div className='max-w-xl px-6 mx-auto mb-2 lg:hidden'>
        <Button
          onClick={() => navigate(PATH_ROUTES.HELP_DESK)}
          className='flex items-center justify-center gap-2 px-0 bg-transparent cursor-pointer hover:bg-transparent text-primary/50 hover:text-primary/50'
        >
          <span>
            <MoveLeft className='w-6 h-6' />
          </span>
          {t('back')}
        </Button>
      </div>

      <div className='flex items-center justify-center w-full h-full'>
        <div className='w-full max-w-xl px-6 mx-auto space-y-8'>
          <p className='text-2xl font-semibold text-center'>
            {t('create_new_ticket')}
          </p>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
              <FormField
                control={form.control}
                name='subject'
                render={({ field }) => (
                  <FormItem className='w-full'>
                    <FormLabel>{t('subject')}</FormLabel>

                    <FormControl>
                      <DropdownMenu>
                        <DropdownMenuTrigger className='flex items-center justify-between w-full h-10 px-4 border-b-2 outline-none border-primary'>
                          <span>{field.value || t('select_issue')}</span>
                          <ChevronDown className='size-5 text-primary' />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          className='w-full !min-w-[33rem]'
                          align='start'
                        >
                          {subjectOptions.map(({ label, tags }) => (
                            <DropdownMenuItem
                              key={label}
                              onClick={() => {
                                form.setValue('subject', label);
                                form.setValue('tags', tags);
                              }}
                            >
                              {label}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='description'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('description')}</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        className='border-t-0 border-b-2 border-l-0 border-r-0 rounded-none border-primary bg-background focus-visible:ring-0 focus-visible:ring-offset-0'
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='priority'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('issue_priority')}</FormLabel>

                    <FormControl>
                      <Select
                        {...field}
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger className='w-full border-t-0 border-b-2 rounded-none border-x-0 border-primary'>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className='!z-[9999]'>
                          {priorities.map((priority) => (
                            <SelectItem value={priority.value}>
                              {priority.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='attachments'
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormLabel>{t('attachments')}</FormLabel>

                      <div>
                        <FormControl>
                          <div className='flex items-center justify-start mb-1 mr-2'>
                            <Input
                              ref={fileInputRef}
                              type='file'
                              onChange={(e) => {
                                const files = e.target.files;
                                if (files) {
                                  const fileList = Array.from(files);
                                  field.onChange([...field.value, ...fileList]);
                                }
                              }}
                              className='hidden border-none'
                              multiple
                              data-testid='file-input'
                            />

                            <Button
                              variant='outline'
                              className='h-7 px-3 !border-medsi-blue text-sm !text-medsi-blue !hover:bg-medsi-blue/10'
                              onClick={handleClick}
                            >
                              <Plus className='mr-2 size-4' />
                              {t('add_attachments')}
                            </Button>
                          </div>
                        </FormControl>

                        {field.value?.length ? (
                          <ScrollArea className='h-16 md:h-12'>
                            <div className='flex flex-wrap items-center justify-start gap-2 mt-2'>
                              {field.value?.map((item: any, index: number) => (
                                <div className='flex items-center justify-center gap-2 px-2 pt-0.5 border border-medsi-blue rounded-xl'>
                                  <p className='text-sm truncate max-w-28 text-medsi-blue'>
                                    {item.name}
                                  </p>
                                  <button
                                    type='button'
                                    className='flex items-center justify-center bg-red-500 rounded-full size-4'
                                    onClick={() => {
                                      const updated = field.value.filter(
                                        (_, ind) => index !== ind
                                      );
                                      field.onChange(updated);
                                    }}
                                  >
                                    <X className='text-white size-3' />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </ScrollArea>
                        ) : null}
                      </div>
                    </FormItem>
                  );
                }}
              />
              <div className='flex items-center justify-center gap-2 !mt-6 md:gap-4'>
                <Button
                  type='submit'
                  className='px-4 h-9'
                  disabled={!form.formState.isValid || isCreating}
                >
                  {isCreating && (
                    <Loader2
                      role='progressbar'
                      className='mr-2 size-4 animate-spin'
                    />
                  )}
                  {t('submit_ticket')}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
}
